import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, useScroll, useTransform } from 'framer-motion'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ShopConfig } from '../lib/supabase'
import { useShop } from '../contexts/ShopContext'
import { useProvider } from '../contexts/ProviderContext'
import { useSubscription } from '../hooks/useSubscription'
import { usePlans } from '../hooks/usePlans'
import { planName } from '../lib/plans'

const Header: React.FC = () => {
  const { user, signOut } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { shops, currentShopId, setCurrentShopId, refreshShops } = useShop()
  const { providers, currentProviderId, setCurrentProviderId, refreshProviders } = useProvider()
  const [shopConfig, setShopConfig] = useState<ShopConfig | null>(null)
  const { scrollY } = useScroll()

  // Scroll-based glass blur
  const bgOpacity = useTransform(scrollY, [0, 60], [0.85, 1])
  const borderOpacity = useTransform(scrollY, [0, 60], [0, 0.06])

  const searchParams = new URLSearchParams(location.search)
  const viewMode = searchParams.get('viewMode')
  const isProviderRoute =
    location.pathname.startsWith('/provider') &&
    !location.pathname.startsWith('/providers') &&
    viewMode !== 'merchant'

  const { planId, isActive } = useSubscription(
    isProviderRoute ? currentProviderId : currentShopId,
    isProviderRoute ? 'provider' : 'merchant'
  )
  const { plans } = usePlans()

  useEffect(() => {
    const loadConfig = async () => {
      if (!user || !currentShopId || isProviderRoute) {
        setShopConfig(null)
        return
      }
      try {
        const { data: cfg } = await supabase
          .from('merchant_config')
          .select('config_object')
          .eq('merchant_id', currentShopId)
          .single()
        setShopConfig(cfg?.config_object as ShopConfig ?? null)
      } catch {
        setShopConfig(null)
      }
    }
    loadConfig()
  }, [user, currentShopId, isProviderRoute])

  const path = location.pathname
  let title = 'FLOWSTOCK'
  let subtitle = shopConfig?.client_name || 'Shop Management'
  let showBack = false
  let backTo = '/dashboard'

  if (path.startsWith('/provider/merchants')) {
    title = 'PROVIDER MERCHANTS'
    subtitle = 'Connection Requests'
    showBack = false
  } else if (path.startsWith('/provider/catalog')) {
    title = 'PROVIDER CATALOG'
    subtitle = providers.find((p) => p.id === currentProviderId)?.name || 'Product Catalog'
  } else if (path.startsWith('/processors')) {
    title = 'PROCESSOR CONFIG'
    subtitle = 'Manage product processing rules'
    showBack = true
  } else if (path.startsWith('/settings')) {
    title = 'SETTINGS'
    subtitle = 'Business Info'
    showBack = true
  } else if (path.startsWith('/zalando')) {
    title = 'ZALANDO'
    subtitle = 'Import products from Zalando'
    showBack = true
  } else if (path.startsWith('/product-lines')) {
    title = 'PRODUCT LINES'
    subtitle = 'Brands and models you sell'
    showBack = true
  } else if (path.startsWith('/providers')) {
    title = 'PROVIDERS'
    subtitle = 'Manage provider connections'
    showBack = true
  } else if (path.startsWith('/payment-plans')) {
    title = 'PAYMENT PLANS'
    subtitle = 'Choose your plan'
  } else if (path.startsWith('/onboarding')) {
    title = 'ONBOARDING'
    subtitle = 'Set up your shop'
  } else if (path.startsWith('/dashboard')) {
    title = 'FLOWSTOCK'
    subtitle = shopConfig?.client_name || 'Shop Management'
  }

  const hasDualAccess = shops.length > 0 && providers.length > 0

  if (!user || path === '/') return null

  return (
    <>
    <motion.header
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}
    >
      {/* Scroll-reactive background */}
      <motion.div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(15,13,11,1)',
          opacity: bgOpacity,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      />
      {/* Bottom border */}
      <motion.div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 1,
          backgroundColor: 'rgba(255,255,255,1)',
          opacity: borderOpacity,
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

          {/* Left: logo + title */}
          <div className="flex items-center gap-4">
            {showBack ? (
              <Link
                to={backTo}
                className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
                aria-label="Back"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </Link>
            ) : (
              <Link to="/dashboard" className="flex items-center gap-2.5">
                <img src="/logo.svg" alt="FLOWSTOCK" className="h-7 w-auto" />
                <span className="hidden sm:inline font-heading font-bold text-white text-base tracking-tight">
                  FLOWSTOCK
                </span>
              </Link>
            )}
            <div className="hidden sm:block h-4 w-px bg-white/[0.12]" />
            <div className="hidden sm:block">
              <p className="text-white/40 text-xs leading-none">{subtitle}</p>
            </div>
          </div>

          {/* Center: view toggle (dual-access users only) */}
          {hasDualAccess && (
            <div className="flex items-center bg-white/[0.06] rounded-full p-0.5">
              <button
                onClick={() => !isProviderRoute || navigate('/dashboard')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  !isProviderRoute
                    ? 'bg-white/[0.12] text-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                Merchant
              </button>
              <button
                onClick={() => isProviderRoute || navigate('/provider/catalog')}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  isProviderRoute
                    ? 'bg-white/[0.12] text-white'
                    : 'text-white/40 hover:text-white/60'
                }`}
              >
                Provider
              </button>
            </div>
          )}

          {/* Right: controls */}
          <div className="flex items-center gap-3">
            {/* Shop / Provider selector */}
            {isProviderRoute ? (
              <div className="relative">
                <select
                  className="glass-select text-xs pr-6"
                  value={currentProviderId || ''}
                  onChange={(e) => setCurrentProviderId(e.target.value || null)}
                  onFocus={() => refreshProviders()}
                >
                  {!providers.length && <option value="">No providers</option>}
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.id}</option>
                  ))}
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            ) : (
              <div className="relative">
                <select
                  className="glass-select text-xs pr-6"
                  value={currentShopId || ''}
                  onChange={(e) => setCurrentShopId(e.target.value || null)}
                  onFocus={() => refreshShops()}
                >
                  {!shops.length && <option value="">No shops</option>}
                  {shops.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.shop_name || s.name || s.client_name || s.shop_domain || s.id}
                    </option>
                  ))}
                </select>
                <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/30 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            )}

            {/* Plan badge (merchant only) */}
            {!isProviderRoute && planId && (
              <span className="hidden md:inline-flex items-center gap-1.5 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-full px-2.5 py-1 text-xs">
                <span className="font-semibold uppercase tracking-wide">{planName(plans, planId)}</span>
                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-orange-400' : 'bg-red-500'}`} />
              </span>
            )}

            {/* User email */}
            <span className="hidden lg:block text-white/30 text-xs truncate max-w-[140px]">
              {user?.email}
            </span>

            {/* Docs */}
            <a
              href="https://flowstock.gitbook.io/untitled-1"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
              title="Documentation"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </a>

            {/* Sign out */}
            <button
              onClick={async () => { try { await signOut() } catch {} }}
              className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
              title="Sign out"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </motion.header>
    {/* Spacer to push content below fixed header */}
    <div className="h-16" />
    </>
  )
}

export default Header
